import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Plus, Trash2, X, Pencil, Store, Phone, Mail, MapPin, Truck, Package, Wrench, Zap, Milk } from "lucide-react";
import { toast } from "sonner";

const TYPE_OPTIONS = [
  { v: "leche", label: "Leche", icon: Milk, color: "bg-[#1E3A8A] text-white" },
  { v: "insumos", label: "Insumos", icon: Package, color: "bg-purple-600 text-white" },
  { v: "servicios", label: "Servicios", icon: Wrench, color: "bg-amber-500 text-white" },
  { v: "fletes", label: "Fletes", icon: Truck, color: "bg-emerald-600 text-white" },
  { v: "refacciones", label: "Refacciones", icon: Zap, color: "bg-orange-600 text-white" },
  { v: "otros", label: "Otros", icon: Store, color: "bg-stone-500 text-white" },
];

const empty = { name: "", contact: "", phone: "", email: "", rfc: "", address: "", types: [], default_credit_days: 0, active: true, notes: "" };

export default function Proveedores() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);
  const [toDelete, setToDelete] = useState(null);
  const [typeFilter, setTypeFilter] = useState("");

  const load = () => api.get("/suppliers").then((r) => setItems(r.data.sort((a, b) => a.name.localeCompare(b.name))));
  useEffect(() => { load(); }, []);

  const openNew = () => { setEditing(null); setForm(empty); setOpen(true); };
  const openEdit = (s) => { setEditing(s); setForm({ ...empty, ...s, types: s.types || [] }); setOpen(true); };

  const toggleType = (t) => setForm((f) => ({ ...f, types: f.types.includes(t) ? f.types.filter((x) => x !== t) : [...f.types, t] }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) return toast.error("Nombre requerido");
    try {
      const payload = { ...form, default_credit_days: parseInt(form.default_credit_days) || 0 };
      if (editing) await api.put(`/suppliers/${editing.id}`, payload);
      else await api.post("/suppliers", payload);
      toast.success(editing ? "Proveedor actualizado" : "Proveedor registrado");
      setOpen(false);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error");
    }
  };

  const del = async () => {
    try {
      await api.delete(`/suppliers/${toDelete.id}`);
      toast.success("Proveedor eliminado");
      setToDelete(null);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error");
    }
  };

  const filtered = typeFilter ? items.filter((s) => (s.types || []).includes(typeFilter)) : items;

  return (
    <div className="max-w-screen-2xl mx-auto p-6 lg:p-8 space-y-6" data-testid="proveedores-page">
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <h1 className="font-outfit font-bold text-3xl tracking-tight text-stone-900 dark:text-stone-100">Proveedores</h1>
          <p className="text-sm text-stone-500 dark:text-stone-400 mt-1">Catálogo unificado de proveedores: leche, insumos, servicios, fletes y otros.</p>
        </div>
        {!open && <Button onClick={openNew} data-testid="new-supplier-btn" className="bg-[#1E3A8A] hover:bg-[#1E40AF]"><Plus className="w-4 h-4 mr-1.5" /> Nuevo proveedor</Button>}
      </div>

      <div className="flex flex-wrap gap-1.5">
        <button onClick={() => setTypeFilter("")} className={`text-xs px-3 py-1.5 rounded-md font-outfit font-medium transition-colors ${typeFilter === "" ? "bg-stone-900 text-white dark:bg-stone-100 dark:text-stone-900" : "bg-stone-100 dark:bg-stone-800 text-stone-700 dark:text-stone-300"}`}>Todos ({items.length})</button>
        {TYPE_OPTIONS.map((t) => {
          const count = items.filter((s) => (s.types || []).includes(t.v)).length;
          const Icon = t.icon;
          return (
            <button key={t.v} onClick={() => setTypeFilter(t.v)} className={`inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-md font-outfit font-medium transition-colors ${typeFilter === t.v ? t.color : "bg-stone-100 dark:bg-stone-800 text-stone-700 dark:text-stone-300"}`}>
              <Icon className="w-3 h-3" /> {t.label} ({count})
            </button>
          );
        })}
      </div>

      {open && (
        <Card className="p-6 border-stone-200 dark:border-stone-800 bg-white dark:bg-stone-950 shadow-sm">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-outfit font-semibold text-lg text-stone-900 dark:text-stone-100">{editing ? "Editar proveedor" : "Nuevo proveedor"}</h2>
            <button onClick={() => setOpen(false)} className="text-stone-400 hover:text-stone-700 dark:hover:text-stone-200"><X className="w-5 h-5" /></button>
          </div>
          <form onSubmit={submit} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Field label="Nombre / Razón social *"><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="supplier-name" /></Field>
              <Field label="RFC"><Input value={form.rfc} onChange={(e) => setForm({ ...form, rfc: e.target.value.toUpperCase() })} placeholder="Opcional" /></Field>
              <Field label="Contacto"><Input value={form.contact} onChange={(e) => setForm({ ...form, contact: e.target.value })} placeholder="Nombre persona contacto" /></Field>
              <Field label="Teléfono"><Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} /></Field>
              <Field label="Email"><Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} /></Field>
              <Field label="Días crédito por defecto"><Input type="number" min="0" value={form.default_credit_days} onChange={(e) => setForm({ ...form, default_credit_days: e.target.value })} placeholder="0 = contado" /></Field>
              <Field label="Dirección" className="md:col-span-2"><Input value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} /></Field>
              <Field label="Notas" className="md:col-span-2"><Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} rows={2} /></Field>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider font-outfit font-semibold text-stone-600 dark:text-stone-400 mb-1.5 block">Tipos (elige al menos uno)</Label>
              <div className="flex flex-wrap gap-1.5">
                {TYPE_OPTIONS.map((t) => {
                  const on = form.types.includes(t.v);
                  const Icon = t.icon;
                  return (
                    <button type="button" key={t.v} onClick={() => toggleType(t.v)} data-testid={`type-${t.v}`} className={`inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-md font-outfit font-medium transition-colors ${on ? t.color : "bg-stone-100 dark:bg-stone-800 text-stone-700 dark:text-stone-300 hover:bg-stone-200 dark:hover:bg-stone-700"}`}>
                      <Icon className="w-3 h-3" /> {t.label}
                    </button>
                  );
                })}
              </div>
            </div>
            <div className="flex gap-2 justify-end pt-2 border-t border-stone-200 dark:border-stone-800">
              <Button type="button" variant="outline" onClick={() => setOpen(false)}>Cancelar</Button>
              <Button type="submit" data-testid="supplier-submit-btn" className="bg-[#1E3A8A] hover:bg-[#1E40AF]">{editing ? "Guardar cambios" : "Registrar"}</Button>
            </div>
          </form>
        </Card>
      )}

      <Card className="border-stone-200 dark:border-stone-800 bg-white dark:bg-stone-950 shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-stone-100 dark:bg-stone-900 border-b border-stone-200 dark:border-stone-800">
              <tr>{["Nombre", "Tipos", "Contacto", "Días crédito", "Acciones"].map((h) => <th key={h} className="px-3 py-2.5 text-left text-[10px] font-outfit font-semibold uppercase tracking-wider text-stone-600 dark:text-stone-400">{h}</th>)}</tr>
            </thead>
            <tbody>
              {filtered.length === 0 ? (
                <tr><td colSpan={5} className="px-4 py-8 text-center text-sm text-stone-500">Sin proveedores registrados.</td></tr>
              ) : filtered.map((s) => (
                <tr key={s.id} className="border-b border-stone-100 dark:border-stone-800 hover:bg-stone-50 dark:hover:bg-stone-900/50" data-testid={`supplier-row-${s.id}`}>
                  <td className="px-3 py-3">
                    <div className="font-medium text-stone-900 dark:text-stone-100">{s.name}</div>
                    {s.rfc && <div className="text-xs font-mono text-stone-500 dark:text-stone-400">{s.rfc}</div>}
                  </td>
                  <td className="px-3 py-3">
                    <div className="flex flex-wrap gap-1">
                      {(s.types || []).length === 0 ? <span className="text-xs text-stone-400">Sin clasificar</span>
                        : s.types.map((t) => {
                          const opt = TYPE_OPTIONS.find((o) => o.v === t);
                          if (!opt) return null;
                          const Icon = opt.icon;
                          return <span key={t} className={`inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded font-outfit font-semibold uppercase tracking-wider ${opt.color}`}><Icon className="w-2.5 h-2.5" /> {opt.label}</span>;
                        })}
                    </div>
                  </td>
                  <td className="px-3 py-3 text-xs text-stone-700 dark:text-stone-300">
                    {s.contact && <div>{s.contact}</div>}
                    {s.phone && <div className="flex items-center gap-1 text-stone-500"><Phone className="w-3 h-3" /> {s.phone}</div>}
                    {s.email && <div className="flex items-center gap-1 text-stone-500"><Mail className="w-3 h-3" /> {s.email}</div>}
                    {!s.contact && !s.phone && !s.email && <span className="text-stone-400">—</span>}
                  </td>
                  <td className="px-3 py-3 text-sm tabular-nums">{s.default_credit_days > 0 ? <span className="inline-block px-2 py-0.5 rounded bg-amber-100 dark:bg-amber-900/40 text-amber-800 dark:text-amber-300 text-xs font-medium">{s.default_credit_days} días</span> : <span className="text-stone-500">Contado</span>}</td>
                  <td className="px-3 py-3">
                    <div className="flex items-center gap-1">
                      <button onClick={() => openEdit(s)} className="p-1.5 rounded-md text-stone-500 hover:text-[#1E3A8A] hover:bg-blue-50 dark:hover:bg-blue-950/40"><Pencil className="w-4 h-4" /></button>
                      <button onClick={() => setToDelete(s)} className="p-1.5 rounded-md text-stone-500 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-950/40"><Trash2 className="w-4 h-4" /></button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>

      <AlertDialog open={!!toDelete} onOpenChange={(o) => !o && setToDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader><AlertDialogTitle>¿Eliminar {toDelete?.name}?</AlertDialogTitle><AlertDialogDescription>Si tiene compras o recepciones asociadas no se podrá eliminar.</AlertDialogDescription></AlertDialogHeader>
          <AlertDialogFooter><AlertDialogCancel>Cancelar</AlertDialogCancel><AlertDialogAction onClick={del} className="bg-red-600 hover:bg-red-700 text-white">Eliminar</AlertDialogAction></AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

const Field = ({ label, children, className = "" }) => (
  <div className={className}>
    <Label className="text-xs uppercase tracking-wider font-outfit font-semibold text-stone-600 dark:text-stone-400">{label}</Label>
    <div className="mt-1.5">{children}</div>
  </div>
);
